import React, { useState, useEffect } from 'react';

const MessageModal = ({ 
  isOpen, 
  onClose, 
  conversationId, 
  onSendMessage, 
  currentUserId, 
  nonce 
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && conversationId) {
      fetchMessages();
    }
  }, [isOpen, conversationId, nonce]);

  const fetchMessages = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `/wp-json/better-messages/v1/thread/${conversationId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch messages');
      const data = await response.json();

      const transformedMessages = (data.messages || [])
        .map(msg => ({
          ...msg,
          sender: data.users.find(user => user.id === msg.sender_id.toString()),
          date: new Date(Math.floor(msg.created_at / 10000) * 1000).toLocaleString()
        }))
        .sort((a, b) => a.created_at - b.created_at);

      setMessages(transformedMessages);

      setTimeout(() => {
        const messageList = document.querySelector('.message-list');
        if (messageList) {
          messageList.scrollTop = messageList.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const messageList = document.querySelector('.message-list');
    if (messageList) {
      messageList.scrollTop = messageList.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const response = await fetch(
        `/wp-json/better-messages/v1/thread/${conversationId}/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce
          },
          body: JSON.stringify({
            message: newMessage,
            temp_id: Date.now().toString(),
            temp_time: Date.now()
          })
        }
      );

      if (!response.ok) throw new Error('Failed to send message');

      setNewMessage('');
      fetchMessages(); 
      if (onSendMessage) {
        onSendMessage();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="message-modal-overlay">
      <div className="message-modal">
        <div className="message-modal-header">
          <h3>Messages</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="message-list">
          {isLoading ? (
            <div className="loading">Loading messages...</div>
          ) : (
            messages.map(msg => {
              const isBookingSystem = msg.sender?.name === 'Booking System';
              const messageClass = `message ${
                isBookingSystem 
                  ? 'booking-system' 
                  : (msg.sender_id === currentUserId ? 'sent' : 'received')
              }`;
              
              return (
                <div 
                  key={msg.message_id} 
                  className={messageClass}
                >
                  <div className="message-sender">{msg.sender?.name || 'Unknown'}</div>
                  <div 
                    className="message-content" 
                    dangerouslySetInnerHTML={{ __html: msg.message }}
                  />
                  <div className="message-meta">{msg.date}</div>
                </div>
              );
            })
          )}
        </div>
        <form className="message-form" onSubmit={handleSendMessage}>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            rows="3"
          />
          <button type="submit" className="send-button">Send</button>
        </form>
      </div>
    </div>
  );
};

export default MessageModal;
